import * as React from 'react';

import {
  RateCollectionApiError,
  type RateSeniorityLevelDto,
} from '@/api/rate-collection-client';
import type { EditLockVariables } from '@/api/use-rate-collection';
import { cellKey } from '@/routes/supplier/rate-collection/saisie-model';

// =============================================================================
// use-edit-lock.ts — Moteur d'EditLock POSTGRES concurrent de la grille (S13, D2 / G0-S13-2).
// =============================================================================
// Concurrence intra-SIRET (plusieurs collaborateurs d'un même fournisseur éditant la même
// submission). Le verrou est PostgreSQL (service D1-B), granularité cellule (profil×niveau).
// Cycle de vie d'un verrou, piloté ICI (décision GATE_0 S13-2, verbatim) :
//   - ACQUISITION au FOCUS de la cellule (acquireLock) ;
//   - RELEASE à la PERTE DE FOCUS (releaseLock) ET à la FERMETURE D'ONGLET (`beforeunload`) ;
//   - HEARTBEAT périodique tant que la cellule est focalisée (repousse l'expiration serveur).
// Pas d'écrasement concurrent : si une cellule est verrouillée par un AUTRE contact, l'acquire
// renvoie 409 CELL_LOCKED (kind 'cell-locked') ⇒ la cellule est SIGNALÉE en lecture seule (badge
// « cellule éditée par … » + tooltip, OPP-04) ; l'autosave n'est pas tenté sur cette cellule.
//
// ZÉRO localStorage (RULE_REF_006) : tout l'état de verrou vit en MÉMOIRE APPLICATIVE (React).

/** Intervalle de heartbeat de la cellule focalisée (sous le TTL serveur par défaut, 5 min). */
const HEARTBEAT_INTERVAL_MS = 60 * 1000;

/** État de verrou d'UNE cellule du point de vue de l'UI. */
export interface CellLockState {
  /** `true` ssi la cellule est verrouillée par un AUTRE contact (⇒ lecture seule signalée). */
  readonly lockedByOther: boolean;
  /** Identifiant du détenteur concurrent (pour le badge « édité par … »), ou `null`. */
  readonly holderContactId: string | null;
}

/** Surface du hook consommée par l'écran / les cellules. */
export interface SaisieEditLock {
  /** État de verrou d'une cellule (jamais `undefined` : défaut = libre). */
  getLock: (profileCode: string, seniorityLevel: RateSeniorityLevelDto) => CellLockState;
  /** À appeler au FOCUS d'une cellule : acquiert le verrou (release de la précédente). */
  onFocusCell: (profileCode: string, seniorityLevel: RateSeniorityLevelDto) => void;
  /** À appeler à la PERTE DE FOCUS : libère le verrou détenu de la cellule. */
  onBlurCell: (profileCode: string, seniorityLevel: RateSeniorityLevelDto) => void;
}

interface UseEditLockParams {
  readonly acquire: (vars: EditLockVariables) => Promise<{
    readonly acquired: boolean;
    readonly holderContactId: string;
  }>;
  readonly release: (vars: EditLockVariables) => Promise<unknown>;
  readonly heartbeat: (vars: EditLockVariables) => Promise<unknown>;
  /** Désactive tout le mécanisme (grille verrouillée / lecture seule deadline). */
  readonly disabled: boolean;
}

/**
 * Moteur d'EditLock. `acquire`/`release`/`heartbeat` sont les mutations TanStack (PUT/POST/DELETE).
 * `disabled` court-circuite tout (submission Soumis ou deadline dépassée ⇒ aucune édition concurrente).
 */
export function useEditLock(params: UseEditLockParams): SaisieEditLock {
  const { acquire, release, heartbeat, disabled } = params;

  // Cellules verrouillées par un AUTRE (clé → holder). Mémoire applicative (jamais localStorage).
  const [foreign, setForeign] = React.useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );

  // Cellule actuellement DÉTENUE par CE contact (au plus une à la fois : focus unique). Ref pour
  // les handlers (beforeunload, heartbeat) sans recréer les callbacks à chaque changement.
  const heldRef = React.useRef<EditLockVariables | null>(null);

  const releaseHeld = React.useCallback((): void => {
    const held = heldRef.current;
    if (held !== null) {
      heldRef.current = null;
      void release(held);
    }
  }, [release]);

  // RELEASE à la FERMETURE D'ONGLET (`beforeunload`) — décision GATE_0 S13-2 (verbatim). Best-effort :
  // l'appel part en arrière-plan ; le navigateur ferme sans attendre (le TTL serveur garantit la
  // libération in fine). On NE bloque PAS la fermeture (pas de preventDefault).
  React.useEffect(() => {
    const onBeforeUnload = (): void => {
      releaseHeld();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      // Au démontage du composant (navigation interne SPA), on libère aussi le verrou détenu.
      releaseHeld();
    };
  }, [releaseHeld]);

  // HEARTBEAT périodique de la cellule détenue (repousse l'expiration tant qu'elle est focalisée).
  React.useEffect(() => {
    if (disabled) {
      return;
    }
    const id = setInterval(() => {
      const held = heldRef.current;
      if (held !== null) {
        void heartbeat(held);
      }
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [heartbeat, disabled]);

  const onFocusCell = React.useCallback(
    (profileCode: string, seniorityLevel: RateSeniorityLevelDto): void => {
      if (disabled) {
        return;
      }
      const target: EditLockVariables = { profileCode, seniorityLevel };
      const key = cellKey(profileCode, seniorityLevel);
      // Libère d'abord le verrou de la cellule précédemment focalisée (focus unique).
      const held = heldRef.current;
      if (held !== null && cellKey(held.profileCode, held.seniorityLevel) !== key) {
        heldRef.current = null;
        void release(held);
      }
      void acquire(target)
        .then((ack) => {
          if (ack.acquired) {
            heldRef.current = target;
            // La cellule n'est plus considérée verrouillée par un autre (on la détient).
            setForeign((prev) => {
              if (!prev.has(key)) {
                return prev;
              }
              const next = new Map(prev);
              next.delete(key);
              return next;
            });
          }
        })
        .catch((err: unknown) => {
          // 409 CELL_LOCKED : profil verrouillé par un AUTRE contact (verrou au grain PROFIL, A0-11) ⇒
          // lecture seule signalée. Le badge affiche le NOM du détenteur (DBU l.2948), résolu par l'API
          // depuis l'annuaire supplier-reference (`holderDisplayName`). JAMAIS un UUID : à défaut de nom
          // résolu, on retombe sur un libellé générique « un autre collaborateur ».
          if (
            err instanceof RateCollectionApiError &&
            err.kind === 'cell-locked'
          ) {
            const holder = err.holderDisplayName ?? 'un autre collaborateur';
            setForeign((prev) => {
              const next = new Map(prev);
              next.set(key, holder);
              return next;
            });
          }
          // Autre erreur (réseau/timeout) : on n'invente PAS un verrou concurrent ; la cellule
          // reste éditable, l'autosave reste l'autorité de cohérence (UPSERT serveur).
        });
    },
    [acquire, release, disabled],
  );

  const onBlurCell = React.useCallback(
    (profileCode: string, seniorityLevel: RateSeniorityLevelDto): void => {
      const held = heldRef.current;
      if (
        held !== null &&
        cellKey(held.profileCode, held.seniorityLevel) ===
          cellKey(profileCode, seniorityLevel)
      ) {
        heldRef.current = null;
        void release(held);
      }
    },
    [release],
  );

  const getLock = React.useCallback(
    (profileCode: string, seniorityLevel: RateSeniorityLevelDto): CellLockState => {
      const holder = foreign.get(cellKey(profileCode, seniorityLevel));
      return {
        lockedByOther: holder !== undefined,
        holderContactId: holder ?? null,
      };
    },
    [foreign],
  );

  return { getLock, onFocusCell, onBlurCell };
}
